import { useCallback, useMemo, useRef, useState } from 'react';

import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { toast } from 'sonner';

import {
  PendingInviteBulkAddResponseDto,
  PendingInviteBulkResultStatus,
} from '../../../../../../../backend/src/auth/pending-matter-invite/dto/pending-invite-bulk-add.response.dto';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useAddPendingInvites } from '@/api/mutations/useAddPendingInvites';
import { useUpdateMatterUsers } from '@/api/mutations/useUpdateMatterUsers';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetUsersInOrg } from '@/api/queries/useGetUsersInOrg';
import { User } from '@/api/types/backend-dto-types';
import { useUserContext } from '@/Contexts/User';
import { useHasPermission } from '@/hooks/useHasPermission';
import { Icon } from '@/new/components/icon';
import { InviteUserDialog } from '@/new/components/invite-user-dialog';
import { SearchInput } from '@/new/components/search-input';
import { Button } from '@/new/components/shadcn/button';
import { ButtonGroup } from '@/new/components/shadcn/button-group';
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from '@/new/components/shadcn/command';
import { Popover, PopoverAnchor, PopoverContent } from '@/new/components/shadcn/popover';
import { Table, TableBody, TableCell, TableRow } from '@/new/components/shadcn/table';
import { cn } from '@/new/lib/utils';

type MatterUser = Pick<User, 'id' | 'email'>;

interface ManageUsersCardProps {
  matterId: string;
}

export function ManageUsersCard({ matterId }: ManageUsersCardProps) {
  const { user: currentUser } = useUserContext();
  const { hasPermission } = useHasPermission();
  const canAddUsers = hasPermission(Permission.MatterAddUsers);
  const canRemoveUsers = hasPermission(Permission.MatterRemoveUsers);

  const { data: matter } = useGetMatterV2(matterId);
  const { data: orgUsers } = useGetUsersInOrg();
  const matterUsers = useMemo<MatterUser[]>(() => matter?.users ?? [], [matter?.users]);
  const creatorId = matter?.createdById;

  const { mutateAsync: updateMatterUsers } = useUpdateMatterUsers(matterId);
  const { mutateAsync: addPendingInvites } = useAddPendingInvites({ matterId });

  const handleAddUser = useCallback(
    async (user: User) => {
      try {
        trackCustomEvent(TrackingEventsV2.MatterAddTeamMemberOnCreator, { userId: user.id });
        await updateMatterUsers({ userIds: [...matterUsers.map((u) => u.id), user.id] });
        toast.success('User added to Matter');
      } catch {
        toast.error('Failed to add user');
      }
    },
    [matterUsers, updateMatterUsers],
  );

  const handleRemoveUser = useCallback(
    async (userId: string) => {
      try {
        await updateMatterUsers({ userIds: matterUsers.filter((u) => u.id !== userId).map((u) => u.id) });
        trackCustomEvent(TrackingEventsV2.MatterRemoveUser, { matterId, userId });
        toast.success('User removed from Matter');
      } catch {
        toast.error('Failed to remove user');
      }
    },
    [matterId, matterUsers, updateMatterUsers],
  );

  const handleInvite = useCallback(
    async (emails: string[]) => {
      trackCustomEvent(TrackingEventsV2.MatterInviteUserNoAccount, {
        userId: currentUser?.id,
        orgId: currentUser?.organisationId,
        inviteCount: emails.length,
      });
      const response = await addPendingInvites(emails);
      const count = (status: PendingInviteBulkResultStatus) =>
        response.results.filter((result) => result.status === status).length;
      const invited = count(PendingInviteBulkResultStatus.Invited);
      const resent = count(PendingInviteBulkResultStatus.Resent);
      const added = count(PendingInviteBulkResultStatus.Added);
      const alreadyAdded = count(PendingInviteBulkResultStatus.AlreadyAdded);
      const failed = count(PendingInviteBulkResultStatus.Failed);
      const completed = response.results.length - failed;

      if (failed > 0) {
        toast.error(
          `${completed} of ${response.results.length} users processed. ${failed} ${failed === 1 ? 'invitation' : 'invitations'} failed.`,
        );
      } else {
        const summary = [
          invited > 0 && `${invited} ${invited === 1 ? 'invitation' : 'invitations'} sent`,
          resent > 0 && `${resent} resent`,
          added > 0 && `${added} existing ${added === 1 ? 'user' : 'users'} added to the Matter`,
          alreadyAdded > 0 && `${alreadyAdded} already on the Matter`,
        ].filter(Boolean);
        toast.success(summary.join(', '));
      }
      return response;
    },
    [addPendingInvites, currentUser?.id, currentUser?.organisationId],
  );

  return (
    <div className="-mx-1.5 space-y-4">
      {canAddUsers && (
        <AddUserSearch
          orgUsers={orgUsers ?? []}
          matterUsers={matterUsers}
          creatorId={creatorId}
          onAddUser={handleAddUser}
          onInvite={handleInvite}
        />
      )}

      <CurrentUsersTable
        matterUsers={matterUsers}
        creatorId={creatorId}
        currentUserId={currentUser?.id}
        canRemoveUsers={canRemoveUsers}
        onRemoveUser={handleRemoveUser}
      />
    </div>
  );
}

interface AddUserSearchProps {
  orgUsers: User[];
  matterUsers: MatterUser[];
  creatorId?: string;
  onAddUser: (user: User) => void;
  onInvite: (emails: string[]) => Promise<PendingInviteBulkAddResponseDto>;
}

type UserEntry = { kind: 'user'; user: User; isAdded: boolean };

function AddUserSearch({ orgUsers, matterUsers, creatorId, onAddUser, onInvite }: AddUserSearchProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [invitePrefill, setInvitePrefill] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Show every (enabled) org user, flagging those already on the Matter so they
  // render greyed out as "Added". Available users sort first so they're easy to pick.
  const orgUserEntries = useMemo<UserEntry[]>(() => {
    const takenIds = new Set(matterUsers.map((u) => u.id));
    if (creatorId) takenIds.add(creatorId);
    return orgUsers
      .filter((u) => !u.disabled)
      .map((user) => ({ kind: 'user' as const, user, isAdded: takenIds.has(user.id) }))
      .sort((a, b) => Number(a.isAdded) - Number(b.isAdded));
  }, [orgUsers, matterUsers, creatorId]);

  const trimmedQuery = query.trim().toLowerCase();

  const filteredUsers = useMemo(() => {
    if (!trimmedQuery) return orgUserEntries;
    return orgUserEntries.filter((e) => e.user.email.toLowerCase().includes(trimmedQuery));
  }, [orgUserEntries, trimmedQuery]);

  const isSelectable = useCallback(
    (index: number) => !!filteredUsers[index] && !filteredUsers[index].isAdded,
    [filteredUsers],
  );

  const selectIndex = useCallback(
    (index: number) => {
      const entry = filteredUsers[index];
      if (!entry || entry.isAdded) return;
      onAddUser(entry.user);
      setQuery('');
      setHighlightedIndex(0);
      setOpen(false);
    },
    [filteredUsers, onAddUser],
  );

  // Open the invite dialog prefilled with whatever the user has typed, so they can
  // confirm or tweak the email before sending the invitation.
  const openInviteDialog = useCallback(() => {
    setInvitePrefill(query.trim());
    setOpen(false);
    setInviteDialogOpen(true);
  }, [query]);

  const handleInvite = useCallback(
    async (emails: string[]) => {
      setQuery('');
      return onInvite(emails);
    },
    [onInvite],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!open || filteredUsers.length === 0) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const dir = e.key === 'ArrowDown' ? 1 : -1;
      setHighlightedIndex((current) => {
        for (let step = 1; step <= filteredUsers.length; step++) {
          const next = (current + dir * step + filteredUsers.length * step) % filteredUsers.length;
          if (isSelectable(next)) return next;
        }
        return current;
      });
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectIndex(highlightedIndex);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <ButtonGroup className="w-full">
          <PopoverAnchor asChild>
            <SearchInput
              ref={inputRef}
              size="sm"
              placeholder="Search for users in your organisation to add to this Matter"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlightedIndex(0);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setTimeout(() => setOpen(false), 120)}
              onKeyDown={handleKeyDown}
              className="z-10 grow rounded-lg p-0.5"
            />
          </PopoverAnchor>
          <Button onClick={openInviteDialog} className="pl-3 text-[13px]">
            <Icon className="lucide--user-plus-2" /> Invite users to Wexler
          </Button>
        </ButtonGroup>

        <PopoverContent
          align="start"
          sideOffset={12}
          alignOffset={-15}
          className="w-lg max-w-(--radix-popover-trigger-width) p-0"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onFocusOutside={(e) => e.preventDefault()}
        >
          <Command shouldFilter={false}>
            <CommandList>
              <CommandEmpty className="mt-2 px-4 py-5 text-center text-[13px] text-gray-500">
                <p>No results in your organisation.</p>
                <p>
                  Would you like to{' '}
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={openInviteDialog}
                    className="cursor-pointer text-blue-500 duration-150 hover:text-blue-600"
                  >
                    invite users to Wexler?
                  </button>
                </p>
              </CommandEmpty>

              <CommandGroup>
                {filteredUsers.map((entry, index) => (
                  <CommandItem
                    key={entry.user.id}
                    value={entry.user.id}
                    onMouseMove={() => !entry.isAdded && setHighlightedIndex(index)}
                    onSelect={() => selectIndex(index)}
                    className={cn(
                      'justify-between px-2 py-0.75 pl-2.5 text-[13px]',
                      entry.isAdded ? 'text-gray-400' : 'cursor-pointer text-gray-600',
                      !entry.isAdded && index === highlightedIndex && 'bg-gray-50 text-gray-900',
                    )}
                  >
                    <span className="lowercase">{entry.user.email}</span>
                    {entry.isAdded ? (
                      <Button variant="ghost" size="sm" className="pointer-events-none text-gray-400">
                        <Icon className="lucide--check" />
                        Added
                      </Button>
                    ) : (
                      <Button variant="ghost" size="sm" className="pointer-events-none">
                        <Icon className="lucide--plus" />
                        Add
                      </Button>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <InviteUserDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        defaultEmail={invitePrefill}
        existingMatterUserEmails={matterUsers.map(({ email }) => email)}
        organisationUserEmails={(orgUsers ?? []).map(({ email }) => email)}
        onInvite={handleInvite}
      />
    </>
  );
}

interface CurrentUsersTableProps {
  matterUsers: MatterUser[];
  creatorId?: string;
  currentUserId?: string;
  canRemoveUsers: boolean;
  onRemoveUser: (userId: string) => void;
}

function CurrentUsersTable({
  matterUsers,
  creatorId,
  currentUserId,
  canRemoveUsers,
  onRemoveUser,
}: CurrentUsersTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');

  const table = useReactTable({
    data: matterUsers,
    columns: [
      {
        accessorKey: 'email',
        header: 'User',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="lowercase">{row.original.email}</span>
            {row.original.id === creatorId && (
              <span className="rounded-md border bg-gray-50 px-1.25 py-0.25 text-xs text-gray-500">Creator</span>
            )}
          </div>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }) => {
          const isCreator = row.original.id === creatorId;
          const isSelf = row.original.id === currentUserId;
          if (!canRemoveUsers) return null;
          return (
            <div className="flex justify-end">
              <Button
                size="sm"
                variant={'ghost'}
                className="p-0"
                disabled={isCreator || isSelf}
                onClick={() => onRemoveUser(row.original.id)}
              >
                <Icon className="lucide--x" />
                Remove
              </Button>
            </div>
          );
        },
      },
    ],
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getRowId: (row) => row.id,
    globalFilterFn: 'includesString',
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 8 } },
  });

  const rows = table.getRowModel().rows;
  const pageCount = table.getPageCount();

  return (
    <>
      <div className="mx-2 mt-5 mr-1 mb-1 flex items-center justify-between gap-2">
        <h2 className="font-medium">Existing Users</h2>

        {pageCount > 1 && (
          <div className="-mb-1 flex items-center justify-end gap-1.5 text-xs text-gray-700">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <Icon className="lucide--chevron-left" />
            </Button>
            <span className="text-gray-500 tabular-nums">
              Page <strong className="text-gray-600">{table.getState().pagination.pageIndex + 1}</strong> of{' '}
              <strong className="text-gray-600">{pageCount}</strong>
            </span>
            <Button size="sm" variant="ghost" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
              <Icon className="lucide--chevron-right" />
            </Button>
          </div>
        )}
      </div>

      <div className="-mx-1.5 mt-1.5 pr-2">
        <Table>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} className="py-6 text-center text-sm text-gray-400">
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell className="px-3.5 py-2.5 text-gray-700" key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </>
  );
}
